import { useReducer } from 'react';
import { createErrorMessage } from './createErrorMessage';
import { useTranslation } from 'react-i18next';
import { validateEntity, Failure } from 'packages/shared/validation';

const useForm = (initialValue = {}, exceptions) => {
    const { t } = useTranslation();

    const reducer = (state, data) => {
        if (data.type && data.type === 'remove' && data.data) {
            const obj = {};

            Object.keys(state).forEach((key) => {
                if (key !== data.data) {
                    obj[key] = state[key];
                }
            });
            return { ...obj };
        }
        if (data.type === 'clean') {
            return Object.keys(state).reduce((fields, key) => {
                fields[key] = null;
                return fields;
            }, {});
        }
        return {
            ...state,
            ...data
        };
    };

    const [fields, setFields] = useReducer(
        reducer,
        Object.keys(initialValue).reduce((fields, key) => {
            fields[key] = null;
            return fields;
        }, {})
    );

    const [errors, setErrors] = useReducer(
        reducer,
        Object.keys(initialValue).reduce((errors, key) => {
            errors[key] = null;
            return errors;
        }, {})
    );

    const formFunctions = {
        onClean: () => setFields({ type: 'clean' }),
        onFieldChange: (data) => {
            setFields({ [data.key]: data.value });
            setErrors({ ...errors, [data.key]: [] });
        },
        onRemove: (data) => {
            setFields({ type: 'remove', data: data });
        },
        fields,
        errors
    };

    const submitFunction = (onlyCurrent) => {
        return new Promise((resolve, reject) => {
            let _errors = {};
            const fieldsToValidate = Object.keys(fields);

            const currentObj = {};
            if (onlyCurrent) {
                fieldsToValidate.forEach((i) => {
                    if (!exceptions.some((item) => item === i)) {
                        currentObj[i] = {
                            required: true
                        };
                    }
                }, {});
            }

            const checks = onlyCurrent ? currentObj : initialValue;

            const validationResults = validateEntity(
                Object.keys(checks).reduce((s, key) => {
                    if (fieldsToValidate.includes(key)) {
                        s[key] = checks[key];
                    }
                    return s;
                }, {}),
                fields
            );

            if (Failure.hasInstance(validationResults)) {
                _errors = validationResults.value.reduce((result, { propertyName, errors }) => {
                    const currentResult = result[propertyName] || [];
                    result[propertyName] = currentResult.concat(
                        errors.map((e) => createErrorMessage(checks[propertyName], e, t))
                    );

                    return result;
                }, {});
            }

            if (
                Object.keys(_errors).reduce(
                    (hasErrors, key) => hasErrors || _errors[key].length > 0,
                    false
                )
            ) {
                setErrors(_errors);
                reject(_errors);
            } else {
                resolve(fields);
            }
        });
    };

    return [formFunctions, submitFunction];
};

export default useForm;
